import classNames from 'classnames';
import type { CSSProperties, ReactElement, ReactNode } from 'react';

import { CircularProgress } from '@mui/material';

import './loading.scss';

export interface WithLoadingProps {
    isLoading: boolean;
    setLoadingDone: (loadingKey?: string) => void;
    setLoadingStart: (loadingKey?: string) => void;
}

interface LoadingProps {
    children?: ReactNode;
    isLoading: boolean;
    style?: CSSProperties;
    clickThrough?: boolean;
    clickThroughOpacity?: number;
}

export function Loading({ children, isLoading, style }: LoadingProps): ReactElement | null {
    if (isLoading) {
        return <CircularProgress data-testid="loadingSpinner" style={style} />;
    }

    return children ? <>{children}</> : null;
}

export function LoadingOverlay({
    children,
    isLoading,
    style,
    clickThrough = false,
    clickThroughOpacity = 0.2,
}: LoadingProps): ReactElement {
    return (
        <div className="loading-overlay" style={{ pointerEvents: clickThrough ? 'none' : 'auto' }}>
            {isLoading && (
                <div className="loading-overlay__indicator-container">
                    <CircularProgress data-testid="loadingSpinner" style={style} />
                </div>
            )}
            <div
                aria-disabled={isLoading}
                className={classNames('loading-overlay__content', {
                    'loading-overlay__content--loading': isLoading,
                })}
                style={clickThrough && isLoading ? { opacity: clickThroughOpacity } : undefined}
            >
                {children}
            </div>
        </div>
    );
}

/**
 * Just display Loading indicator for now, if needed we can display
 * 'Loading' text along with this (which should be translatable)
 * @returns Loading Indicator
 */
export function SuspenseFallback(): ReactElement {
    return (
        <div className="loading-overlay__suspense">
            <div className="loading-overlay__indicator-container">
                <CircularProgress />
            </div>
        </div>
    );
}
